import { useMutation, useQuery } from 'react-query';
import {
	AuthSource,
	IReqVerifyToken,
	IResTokenExchange,
	IResAuthorized,
	IUnauthorizedResponse,
	UnauthorizedReason
} from 'types/api';

import { post, get, HTTP_STATUS_CODE } from 'api/api';
import axios, { AxiosError } from 'axios';
import useToast from 'hooks/useToast';
import { StorageK } from 'keys';
import { ToastSeverity } from 'types/toast';
import { descopeApiOrigin } from 'utils/url';
import QueryKeys from './queryKeys';
import { API_ROUTES } from './apiRoutes';
import { defaultInstance } from './setup';

const authorized = async (
	projectId: string,
	tenantId: string,
	ssoId: string,
	cookieName: string
): Promise<IResAuthorized> => {
	// set relevant headers for the SSO Suite
	defaultInstance.defaults.headers.common['x-Current-Project'] =
		projectId || '';
	defaultInstance.defaults.headers.common['x-sso-suite-tenant-id'] =
		tenantId || '';
	defaultInstance.defaults.headers.common['x-sso-suite-sso-id'] = ssoId || '';
	defaultInstance.defaults.headers.common['x-descope-refresh-cookie-name'] =
		cookieName || '';

	// set authorization header if dsr token is available in local storage
	const dsrToken = localStorage.getItem('DSR');
	if (dsrToken) {
		defaultInstance.defaults.headers.common.Authorization = `Bearer ${dsrToken}`;
	}

	const res = await get<{
		tenantId: string;
		domainVerificationTxt?: string | null;
		authSource?: AuthSource | null;
	}>(API_ROUTES.authorized);

	defaultInstance.defaults.headers.common['x-csrf-token'] =
		(res.headers && res.headers['x-csrf-token']) || '';

	// set the provided values in local storage so they won't need to be passed every time
	localStorage.setItem(StorageK.ProjectId, projectId || '');
	localStorage.setItem(StorageK.TenantId, tenantId || '');
	localStorage.setItem(StorageK.SsoId, ssoId || '');
	localStorage.setItem(StorageK.CookieName, cookieName || '');

	return {
		verified: true,
		projectId,
		done: true,
		tenantId: res.data.tenantId,
		unauthorizedReason: null,
		domainVerificationTxt: res.data.domainVerificationTxt ?? null,
		authSource: res.data.authSource ?? null
	};
};

const verifyToken = async (
	enabled: boolean,
	token: string,
	projectId: string,
	tenantId: string,
	ssoId: string,
	cookieName: string
): Promise<IResAuthorized | null> => {
	if (!enabled) {
		return null;
	}

	if (!projectId) {
		return {
			verified: false,
			done: true,
			projectId: null,
			tenantId: null,
			unauthorizedReason: null,
			domainVerificationTxt: null
		};
	}

	if (!token) {
		try {
			return await authorized(projectId, tenantId, ssoId, cookieName); // there is a case of localStorage or a case that we come without token but with project ID in the URL, case of using Descope JWT
		} catch (err) {
			if ((err as AxiosError).status === HTTP_STATUS_CODE.unauthorized) {
				const errorData = (err as AxiosError<IUnauthorizedResponse>)?.response
					?.data;
				const reason =
					errorData?.unauthorizedReason || UnauthorizedReason.NO_AUTH;
				return {
					verified: false,
					projectId,
					tenantId,
					done: true,
					unauthorizedReason: reason,
					domainVerificationTxt: null
				};
			}
			/* istanbul ignore next: proceed to queryClient handling  */
			throw err;
		}
	}

	try {
		await post<IReqVerifyToken, IResTokenExchange>(API_ROUTES.exchangeToken, {
			token,
			projectId
		});
		return await authorized(projectId, tenantId, ssoId, cookieName);
	} catch (err) {
		if ((err as AxiosError).status === HTTP_STATUS_CODE.unauthorized) {
			const errorData = (err as AxiosError<IUnauthorizedResponse>)?.response
				?.data;
			const reason =
				errorData?.unauthorizedReason || UnauthorizedReason.NO_AUTH;
			return {
				verified: false,
				projectId,
				tenantId,
				done: true,
				unauthorizedReason: reason,
				domainVerificationTxt: null
			};
		}
		/* istanbul ignore next: proceed to queryClient handling  */
		throw err;
	}
};

export const useVerifyToken = (
	enabled: boolean,
	token: string,
	projectId: string,
	tenantId: string,
	ssoId: string,
	cookieName: string,
	onSuccess: (data: IResAuthorized) => void
) =>
	useQuery({
		queryKey: [QueryKeys.token, projectId, tenantId, ssoId],
		queryFn: () =>
			verifyToken(enabled, token, projectId, tenantId, ssoId, cookieName),
		staleTime: Infinity,
		onSuccess: (data) => {
			if (data) {
				onSuccess(data);
			}
		}
	});

// a DSR session belongs to the project, so the sso-app API cannot end it; the Descope auth API can
const logoutDescopeSession = async (projectId: string) => {
	const dsr = localStorage.getItem('DSR');
	await axios.post(
		`${descopeApiOrigin()}/v1/auth/logout`,
		{},
		{
			headers: { Authorization: `Bearer ${projectId}${dsr ? `:${dsr}` : ''}` },
			withCredentials: true,
			allowAbsoluteUrls: true
		}
	);
	localStorage.removeItem('DS');
	localStorage.removeItem('DSR');
	delete defaultInstance.defaults.headers.common.Authorization;
};

export const useLogout = (onSuccess: () => void) => {
	const { addToast } = useToast();

	return useMutation({
		mutationFn: async ({
			authSource,
			projectId
		}: Pick<IResAuthorized, 'authSource' | 'projectId'>) => {
			if (authSource === AuthSource.DSRSSO) {
				await post(API_ROUTES.logout, {});
			} else if (projectId) {
				await logoutDescopeSession(projectId);
			}
		},
		onSuccess,
		onError: () => {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('app.navbar.logoutError')
			});
		}
	});
};
